import * as R from "ramda";
import { useDispatch, useSelector } from "react-redux";
import {
  createTable,
  fetchTableCount,
  fetchTablePage,
  getTable,
  getTableLogs,
  getTableRunControl,
  getTableRunHistory,
  getTableSpec,
  runTable,
  setTableCode,
  setTableRunControl as setTableRunControlApi,
  setTableTrigger,
  updateTableColumns,
  updateTableInputs,
} from "../api/table";
import { useAppId } from "../hook";
import {
  ActionType,
  AddTablePageConfig,
  AppDispatch,
  RootState,
} from "../reducer";
import { SetTableLoadingAction } from "../reducer/table";
import {
  CodeType,
  GetTableRunHistoryResponse,
  RunControl,
  RunControlSetting,
  SearchTableRequest,
  Table,
  TableColumn,
  TableLogsMap,
  TableNotFound,
  TableNotFoundMap,
  TableOrNotFound,
  TableRunControlMap,
  TableRunHistoryMap,
  TableRunResponses,
  TableRunSuccess,
  TableSpec,
  TableSpecMap,
  TableTrigger,
  TableType,
  UpdateTableColumnsRequest,
  UpdateTableInputsRequest,
} from "../types/table";
import {
  useFetchNotebookPage,
  useNotebook,
  useSelectedNotebookPageId,
} from "./page";
import { getPages } from "../api/page";
import { useAppIdMap } from "./app";
import { useEffect, useMemo, useState } from "react";
import {
  makeSelectInputTables,
  selectFileIdToTable,
  selectNotFoundTables,
  selectTableLoadingMap,
  selectTableLogsMap,
  selectTableRunControlMap,
  selectTableRunHistoryMap,
  selectTableRunResponses,
  selectTableSpecs,
  selectTables,
} from "../selectors/table";

export const useTableRunResponses: () => [
  TableRunResponses,
  (tableRunResponses: TableRunResponses) => void
] = () => {
  const tableRunResponses = useSelector(selectTableRunResponses);
  const dispatch = useDispatch<AppDispatch>();
  const setTableRunResponses = (tableRunResponses: TableRunResponses) =>
    dispatch({ type: ActionType.SET_TABLE_RUN_RESPONSES, tableRunResponses });
  return [tableRunResponses, setTableRunResponses];
};

export const useTableSpecs: () => [
  TableSpecMap,
  (tableSpecs: TableSpecMap) => void
] = () => {
  const tableSpecs = useSelector(selectTableSpecs);
  const dispatch = useDispatch<AppDispatch>();
  const setTableSpecs = (tableSpecs: TableSpecMap) =>
    dispatch({ type: ActionType.SET_TABLE_SPECS, tableSpecs });
  return [tableSpecs, setTableSpecs];
};

export const useTableRunResponse: (
  tableId: string
) => TableRunSuccess | undefined = (tableId) => {
  const [tableRunResponses, setTableRunResponses] = useTableRunResponses();
  return tableRunResponses[tableId];
};

export const useFetchTableSpecs: () => (config: {
  tableIds: string[];
}) => Promise<TableSpecMap> = () => {
  const [appIdMaybe] = useAppId();
  const appId = appIdMaybe!;
  const [tableSpecs, setTableSpecs] = useTableSpecs();

  const fetchTableSpecs: (config: {
    tableIds: string[];
    appId: string;
  }) => Promise<TableSpec[]> = ({ appId, tableIds }) => {
    return Promise.all(
      R.map((tableId: string) => getTableSpec({ appId, tableId }), tableIds)
    );
  };

  return async ({ tableIds }) => {
    const tableSpecsResponse = await fetchTableSpecs({
      tableIds,
      appId: appId!,
    });
    const addedTableSpecs: TableSpecMap = Object.fromEntries(
      R.map(
        (tableSpec) => [tableSpec["table-id"], tableSpec],
        tableSpecsResponse
      )
    );
    const newTableSpecs = { ...tableSpecs, ...addedTableSpecs };
    setTableSpecs(newTableSpecs);
    return addedTableSpecs;
  };
};

export const useAddNotFoundTables: () => (
  notFoundTables: TableNotFound[]
) => void = () => {
  const dispatch = useDispatch();
  const addNotFoundTables = (notFoundTables: TableNotFound[]) => {
    dispatch({ type: ActionType.ADD_NOT_FOUND_TABLES, notFoundTables });
  };
  return addNotFoundTables;
};

export const useAddTables: () => (tables: Table[]) => void = () => {
  const dispatch = useDispatch();
  const addTablePages = useAddTablePages();
  const addTables = (tables: Table[]) => {
    dispatch({ type: ActionType.ADD_TABLES, tables });
    tables.forEach((table) => {
      if (table.rows) {
        addTablePages({
          tableId: table["table-id"],
          index: 0,
          rows: table.rows,
        });
      }
    });
  };
  return addTables;
};

export const useAddTablePages: () => (
  config: AddTablePageConfig
) => void = () => {
  const dispatch = useDispatch();
  const addTablePages = (config: AddTablePageConfig) => {
    dispatch({ type: ActionType.ADD_TABLE_PAGE, ...config });
  };
  return addTablePages;
};

export const useNotFoundTables: () => TableNotFoundMap = () => {
  const notFoundTables = useSelector(selectNotFoundTables);
  return notFoundTables;
};

export const useIsTableNotFound: () => (tableId?: string) => boolean = () => {
  const notFoundTables = useNotFoundTables();

  return (tableId) => {
    return tableId !== undefined && tableId in notFoundTables;
  };
};

export const useFetchTables: () => (config: {
  requests: SearchTableRequest[];
}) => Promise<Table[]> = () => {
  const addTables = useAddTables();
  const fetchTables: (config: {
    requests: SearchTableRequest[];
  }) => Promise<Table[]> = ({ requests }) => {
    return Promise.all(
      R.map((request: SearchTableRequest) => getTable(request), requests)
    );
  };

  return async ({ requests }) => {
    const tables = await fetchTables({ requests });
    addTables(tables);
    return tables;
  };
};

export const useFetchTablesFromSpecs: () => (config: {
  tableSpecs: TableSpecMap;
}) => Promise<Table[]> = () => {
  const fetchTables = useFetchTables();
  const { appId } = useAppIdMap();

  return async ({ tableSpecs }) => {
    const tableIds = Object.values(tableSpecs)
      .filter((tableSpec) => !tableSpec["execute-error"])
      .map((tableSpec) => tableSpec["table-id"]);
    const requests: SearchTableRequest[] = tableIds.map((tableId) => ({
      "app-id": appId!,
      "table-id": tableId,
    }));
    return fetchTables({ requests });
  };
};

export const useRunTable: () => (config: {
  tableId: string;
}) => Promise<any> = () => {
  const [tableRunResponses, setTableRunResponses] = useTableRunResponses();
  const [appId] = useAppId();
  const fetchTableLogs = useFetchTableLogs();
  const fetchTableSpecs = useFetchTableSpecs();
  const fetchTableRunHistory = useFetchTableRunHistory();
  const setIsLoading = useSetTableIsLoading();

  return ({ tableId }) => {
    setIsLoading({ tableId, loading: true });
    return runTable({ tableId, appId: appId! })
      .then((res) => {
        const newTableRunResponses = { ...tableRunResponses, [tableId]: res };
        setTableRunResponses(newTableRunResponses);
        fetchTableSpecs({ tableIds: [tableId] });
        fetchTableRunHistory(tableId);
        return fetchTableLogs(tableId);
      })
      .finally(() => {
        setIsLoading({ tableId, loading: false });
      });
  };
};

export const useSetTableTrigger: () => (config: {
  tableId: string;
  trigger: TableTrigger;
}) => void = () => {
  const [appId] = useAppId();
  const [notebook, setNotebook] = useNotebook();

  return ({ tableId, trigger }) => {
    setTableTrigger({ tableId, trigger }).then((res) => {
      getPages({ "app-id": appId! }).then((notebook) => {
        setNotebook(notebook);
      });
    });
  };
};

export const useSetTableCode: () => (config: {
  tableId: string;
  code: string;
  fetchAfter?: boolean;
}) => Promise<any> = () => {
  const fetchTableSpecs = useFetchTableSpecs();

  return ({ tableId, code, fetchAfter }) =>
    setTableCode({ tableId, code }).then((res) =>
      fetchAfter ? fetchTableSpecs({ tableIds: [tableId] }) : undefined
    );
};

export const useTableLogsMap: () => [
  TableLogsMap,
  (tableRunResponses: TableLogsMap) => void
] = () => {
  const tableLogsMap = useSelector(selectTableLogsMap);
  const dispatch = useDispatch<AppDispatch>();
  const setTableLogsMap = (tableLogsMap: TableLogsMap) =>
    dispatch({ type: ActionType.SET_TABLE_LOGS_MAP, tableLogsMap });
  return [tableLogsMap, setTableLogsMap];
};

export const useSetTableLogs: () => (config: {
  tableId: string;
  logs: string;
}) => void = () => {
  const [tableLogsMap, setTableLogsMap] = useTableLogsMap();
  return ({ tableId, logs }) => {
    const newTableLogsMap = { ...tableLogsMap, [tableId]: logs };
    setTableLogsMap(newTableLogsMap);
  };
};

export const useGetTableLogsForTable: () => (
  tableId?: string
) => string = () => {
  const [tableLogsMap, setTableLogsMap] = useTableLogsMap();

  return (tableId) => {
    return tableId ? tableLogsMap[tableId] || "" : "";
  };
};

export const useFetchTableLogs: () => (
  tableId: string
) => Promise<string> = () => {
  const setTableLogs = useSetTableLogs();
  const [appId, setAppId] = useAppId();

  return (tableId) => {
    return getTableLogs({ tableId, appId: appId! }).then((res) => {
      setTableLogs({ tableId, logs: res.logs });
      return res.logs;
    });
  };
};

export const useTables = () => {
  const tables = useSelector(selectTables);
  return tables;
};

export const useTable: (tableId?: string) => Table | undefined = (tableId) => {
  const table = useSelector((state: RootState) =>
    tableId ? state.tables.tables?.[tableId] : undefined
  );
  return table;
};

export const useTableInputs: (tableId?: string) => TableOrNotFound[] = (
  tableId
) => {
  const tableSpec = useTableSpec(tableId);
  const inputFileIds = tableSpec?.inputs ?? [];
  const selectInputTables = useMemo(
    () => makeSelectInputTables(inputFileIds),
    // eslint-disable-next-line
    [inputFileIds.join(",")]
  );
  const inputTables = useSelector(selectInputTables);
  return inputTables;
};

export const useTableFromFileId: () => (
  fileId: string
) => TableOrNotFound = () => {
  const fileIdToTable = useSelector(selectFileIdToTable);

  return (fileId) => {
    const table = fileIdToTable?.[fileId] ?? {
      "not-found": true,
      "file-id": fileId,
    };
    return table;
  };
};

export const useTableSpec: (tableId?: string) => TableSpec | undefined = (
  tableId
) => {
  const tableSpec = useSelector((state: RootState) =>
    tableId ? state.tables.tableSpecs[tableId] : undefined
  );
  return tableSpec;
};

export const useTableType: (tableId?: string) => TableType = (tableId) => {
  const tableSpec = useTableSpec(tableId);
  const table = useTable(tableId);
  if (tableSpec) {
    return TableType.CODE_TABLE;
  }
  if (table) {
    return TableType.DATA_TABLE;
  }
  return TableType.LOADING;
};

export const useTableRunHistoryMap: () => [
  TableRunHistoryMap,
  (TableRunHistoryMap: TableRunHistoryMap) => void
] = () => {
  const tableRunHistoryMap = useSelector(selectTableRunHistoryMap);
  const dispatch = useDispatch<AppDispatch>();
  const setTableLogsMap = (tableRunHistoryMap: TableRunHistoryMap) =>
    dispatch({
      type: ActionType.SET_TABLE_RUN_HISTORY_MAP,
      tableRunHistoryMap,
    });
  return [tableRunHistoryMap, setTableLogsMap];
};

export const useFetchTableRunHistory: () => (
  tableId: string
) => Promise<any> = () => {
  const [tableRunHistoryMap, setTableRunHistoryMap] = useTableRunHistoryMap();
  const [appId, setAppId] = useAppId();

  return async (tableId: string) => {
    getTableRunHistory({ appId: appId!, tableId }).then((response) => {
      setTableRunHistoryMap({ ...tableRunHistoryMap, [tableId]: response });
      return response;
    });
  };
};

export const useTableRunHistory: (
  tableId?: string
) => GetTableRunHistoryResponse | undefined = (tableId) => {
  const [tableRunHistoryMap, setTableRunHistoryMap] = useTableRunHistoryMap();

  return tableId ? tableRunHistoryMap[tableId] : undefined;
};

export const useTableRunControlMap: () => [
  TableRunControlMap,
  (config: RunControlSetting) => void
] = () => {
  const tableRunControlMap = useSelector(selectTableRunControlMap);
  const dispatch = useDispatch<AppDispatch>();
  const setTableRunControlMap = ({ tableId, runControl }: RunControlSetting) =>
    dispatch({
      type: ActionType.SET_TABLE_RUN_CONTROL,
      tableId,
      runControl,
    });
  return [tableRunControlMap, setTableRunControlMap];
};

export const useTableRunControl: (tableId: string) => RunControl | undefined = (
  tableId: string
) => {
  const [tableRunControlMap, setTableRunControlMap] = useTableRunControlMap();
  const tableRunControl =
    tableId in tableRunControlMap ? tableRunControlMap[tableId] : undefined;
  return tableRunControl;
};

export const useFetchTableRunControl: () => (
  tableId: string
) => Promise<RunControl> = () => {
  const [appId] = useAppId();
  const [tableRunControlMap, setTableRunControl] = useTableRunControlMap();

  return (tableId: string) => {
    return getTableRunControl({ tableId, appId: appId! }).then((response) => {
      const runControl = response["run-control"];
      setTableRunControl({ tableId, runControl });
      return runControl;
    });
  };
};

export const useSetTableRunControl: () => (
  config: RunControlSetting
) => Promise<void> = () => {
  const [appId] = useAppId();
  const [tableRunControlMap, setTableRunControl] = useTableRunControlMap();

  return ({ tableId, runControl }: RunControlSetting) =>
    setTableRunControlApi({ appId: appId!, tableId, runControl }).then(
      (response) => {
        setTableRunControl({ tableId, runControl });
      }
    );
};

export const useTableIsLoading: (
  tableId?: string
) => [boolean, (isLoading: boolean) => void] = (tableId) => {
  const tableLoadingMap = useSelector(selectTableLoadingMap);
  const dispatch = useDispatch<AppDispatch>();
  const isLoading =
    tableId && tableId in tableLoadingMap ? tableLoadingMap[tableId] : false;

  const setIsLoading = (loading: boolean) => {
    if (!tableId) {
      throw new Error("Table id not defined");
    }

    const action: SetTableLoadingAction = {
      type: ActionType.SET_TABLE_LOADING,
      loading,
      tableId,
    };
    dispatch(action as any);
  };

  return [isLoading, setIsLoading];
};

export const useSetTableIsLoading: () => (config: {
  tableId: string;
  loading: boolean;
}) => void = () => {
  const dispatch = useDispatch<AppDispatch>();

  return ({ loading, tableId }) => {
    const action: SetTableLoadingAction = {
      type: ActionType.SET_TABLE_LOADING,
      loading,
      tableId,
    };
    dispatch(action as any);
  };
};

// export const useCreateTable: () => (config: {
//   tableName: string;
//   codeType: CodeType;
// }) => Promise<any> = () => {
//   const [appId] = useAppId();
//   const [pageId] = useSelectedNotebookPageId();
//   const fetchNotebookPage = useFetchNotebookPage();

//   return (config) => {
//     if (!appId) throw new Error("No AppId");
//     if (!pageId) throw new Error("No PageId");

//     const createTableConfig = { ...config, appId, pageId };

//     return createTable(createTableConfig).then((res) => {
//       fetchNotebookPage(pageId);
//     });
//   };
// };

export const useSaveTableColumns: () => (config: {
  tableId: string;
  columns: TableColumn[];
}) => Promise<any> = () => {
  const [appId] = useAppId();
  const fetchTableSpecs = useFetchTableSpecs();

  return (config) => {
    if (!appId) throw new Error("No AppId");

    const updateTableConfig: UpdateTableColumnsRequest = {
      ...config,
      appId,
    };

    return updateTableColumns(updateTableConfig).then((res) => {
      fetchTableSpecs({ tableIds: [config.tableId] });
    });
  };
};

export const useSaveTableInputs: () => (config: {
  tableId: string;
  inputs: string[];
}) => Promise<any> = () => {
  const [appId] = useAppId();
  const fetchTableSpecs = useFetchTableSpecs();

  return (config) => {
    if (!appId) throw new Error("No AppId");

    const updateTableConfig: UpdateTableInputsRequest = {
      ...config,
      appId,
    };

    return updateTableInputs(updateTableConfig).then((res) => {
      fetchTableSpecs({ tableIds: [config.tableId] });
    });
  };
};

export const useAddTableInputsOptions: (config: {
  tableId?: string;
}) => Table[] = ({ tableId }) => {
  const [appId] = useAppId();
  const tables = useTables();
  const tableSpec = useTableSpec(tableId);

  if (!tableId) return [];
  const activeTable = tables[tableId];

  if (!activeTable) return [];
  const activeFileId = activeTable["file-id"];

  const currentInputs = new Set(tableSpec?.inputs ?? []);
  const isValidInput = (fileId: string) =>
    !currentInputs.has(fileId) && activeFileId !== fileId;

  const addOptions = Object.values(tables).filter((table) =>
    isValidInput(table["file-id"])
  );

  return addOptions;
};

export const useTableSearch = (
  tableId: string | undefined,
  config?: { onPageCallBack?: (page: Table) => void }
) => {
  const onPageCallBack = config?.onPageCallBack;
  const [index, setIndex] = useState<number>(0);
  const [rows, setRows] = useState<string[][]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const page = index;
  const setPage = (page: number) => setIndex(page);
  const [tableCount, setTableCount] = useState<number>(0);
  const [appId, setAppId] = useAppId();
  const [header, setHeader] = useState<string[] | undefined>(undefined);

  const fetchTable = () => {
    if (!appId) return;
    if (!tableId) return;

    setIsLoading(true);

    const countPromise = fetchTableCount({ appId: appId, tableId }).then(
      (res) => {
        setTableCount(res.count);
      }
    );
    const skip = 10 * index;
    const limit = 10;
    const rowPromise = fetchTablePage({
      "app-id": appId,
      "table-id": tableId,
      skip,
      limit,
    }).then((res) => {
      onPageCallBack && onPageCallBack(res);
      setRows(res.rows!);
      setHeader(res.header!);
    });

    Promise.all([countPromise, rowPromise]).then(() => {
      setIsLoading(false);
    });
  };

  useEffect(() => {
    if (!appId || !tableId) return;

    fetchTable();
  }, [appId, tableId, index]);

  return { fetchTable, rows, isLoading, tableCount, setPage, page, header };
};
